import React, { useState, useEffect,useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  Globe,
  GraduationCap,
  Building,
  Save,
  Loader2,
  Linkedin,
  Twitter,
  Github,
  Plus,
  X,
  MapPin,
  Book,
  CheckCircle2,
  AlertCircle,
  Hash,
  Trophy,
  Lock
} from "lucide-react";
import ImageUpload from "../components/ImageUpload";

// Component Definitions
const TabButton = ({ id, label, icon: Icon, active, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={`
      flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm
      transition-all duration-200 relative
      ${
        active
          ? "text-blue-600 bg-blue-50"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      }
    `}
  >
    <Icon className="h-5 w-5" />
    <span>{label}</span>
    {active && (
      <motion.div
        layoutId="activeTab"
        className="absolute -right-px inset-y-0 w-1 bg-blue-600 rounded-l"
        initial={false}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      />
    )}
  </button>
);

const FormSection = ({ title, description, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-gray-500 mb-4">{description}</p>
      )}
      {children}
    </div>
  </div>
);

const InputField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  icon: Icon,
  error,
  helper,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        className={`
          block w-full rounded-lg border-gray-300 
          ${Icon ? "pl-10" : "pl-4"} 
          ${error ? "border-red-300 focus:ring-red-500" : "focus:ring-blue-500"}
          shadow-sm transition-colors duration-200
        `}
        placeholder={placeholder}
      />
    </div>
    {(error || helper) && (
      <p className={`mt-1 text-sm ${error ? "text-red-600" : "text-gray-500"}`}>
        {error || helper}
      </p>
    )}
  </div>
);
const LocationInput = ({ value, onChange, onSelectLocation }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  
  // Debounced search function
  useEffect(() => {
    if (!value || value.length < 3) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/serp/maps?q=${encodeURIComponent(value)}`
        );
        
        if (!response.ok) throw new Error('Search failed');
        
        const data = await response.json();
        
        // Handle both place_results and local_results
        let results = [];
        if (data.place_results) {
          results.push({
            title: data.place_results.title,
            address: data.place_results.address,
            coordinates: [
              data.place_results.gps_coordinates.longitude,
              data.place_results.gps_coordinates.latitude
            ]
          });
        }
        if (data.place_results?.at_this_location) {
          results = [
            ...results,
            ...data.place_results.at_this_location.map(place => ({
              title: place.title,
              address: place.address || data.place_results.address,
              coordinates: [
                place.gps_coordinates.longitude,
                place.gps_coordinates.latitude
              ]
            }))
          ];
        }
        setSearchResults(results);
        setShowResults(true);
      } catch (error) {
        console.error('Location search failed:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [value]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationSelect = (location) => {
    onSelectLocation(location);
    setShowResults(false);  // Close dropdown after selection
    setSearchResults([]); // Clear results
  };

  return (
    <div className="relative" ref={inputRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Current Location
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your location (e.g., Ahmedabad, Gujarat)"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => value.length >= 3 && setShowResults(true)}
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin h-5 w-5 text-gray-400">
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          </div>
        )}
      </div>
      
      {/* Search Results Dropdown */}
      {showResults && searchResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 overflow-auto">
          {searchResults.map((result, index) => (
            <button
              key={index}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
              onClick={() => handleLocationSelect({
                address: `${result.title}, ${result.address}`,
                coordinates: result.coordinates
              })}
            >
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">{result.title}</div>
                  <div className="text-sm text-gray-500">{result.address}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
const PasswordChangeSection = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handlePasswordChange = async () => {
    // Remove the e.preventDefault() since we're no longer using a form
    setError("");
    setSuccess("");

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError("New passwords don't match");
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to change password");
      }

      setSuccess("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-md">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}
      {success && (
        <div className="p-3 rounded-lg bg-green-50 text-green-600 text-sm flex items-center">
          <CheckCircle2 className="h-4 w-4 mr-2" />
          {success}
        </div>
      )}

      <div className="grid gap-4">
        <InputField
          label="Current Password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Enter your current password"
          required
        />
        
        <InputField
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password"
          helper="Password must be at least 6 characters long"
          required
        />
        
        <InputField
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          required
        />
      </div>

      <div className="flex justify-end mt-6">
        <button
          type="button" // Changed from submit to button
          onClick={handlePasswordChange}
          disabled={loading}
          className={`
            inline-flex items-center px-4 py-2 border border-transparent 
            rounded-lg shadow-sm text-sm font-medium text-white
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}
          `}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
              Changing Password...
            </>
          ) : (
            "Change Password"
          )}
        </button>
      </div>
    </div>
  );
};

const ProfileEdit = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("personal");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [locationResults, setLocationResults] = useState(null);
  const [college, setCollege] = useState(null);
  useEffect(() => {
    const fetchCollegeData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (!response.ok) throw new Error("Failed to fetch college data");
        const data = await response.json();
        setCollege(data);
      } catch (err) {
        console.error("Error fetching college data:", err);
      }
    };
  
    fetchCollegeData();
  }, []);


  // Tabs configuration
  const tabs = [
    {
      id: "personal",
      label: "Personal Info",
      icon: User,
      description: "Manage your personal information and contact details",
    },
    {
      id: "academic",
      label: "Academic",
      icon: GraduationCap,
      description: "Your academic background and achievements",
    },
    {
      id: "professional",
      label: "Professional",
      icon: Briefcase,
      description: "Work experience and professional skills",
    },
    {
      id: "social",
      label: "Social Profiles",
      icon: Globe,
      description: "Your presence across the web",
    },
    {
      id: "security",
      label: "Security",
      icon: Lock,  // Import Lock from lucide-react
      description: "Manage your password and security settings",
    },
  ];

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    department: "",
    enrollmentNumber: "",
    graduationYear: "",
    personalInfo: {
      middleName: "",
      dateOfBirth: "",
      gender: "",
      contactNumber: "",
      alternateEmail: "",
      avatar: "",
      currentLocation: {
        type: "Point",
        coordinates: [0, 0], // [longitude, latitude]
        address: ""
      }

    },
    academicInfo: {
      batch: {
        startYear: "",
      },
      degree: {
        name: "", // Changed from string to object
      },
      department: {
        name: "", // Changed from string to object
      },
      achievements: [],
    },
    professionalInfo: {
      currentlyEmployed: false,
      experiences: [],
      skills: [],
      resumeUrl: "",
    },
    socialLinks: {
      linkedin: "",
      twitter: "",
      github: "",
      portfolio: "",
    },
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/alumni/profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch profile");
      const data = await response.json();
      setProfile(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/alumni/profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(profile),
        }
      );

      if (!response.ok) throw new Error("Failed to update profile");

      const data = await response.json();
      setProfile(data);
      setSuccessMessage("Profile updated successfully");
      setHasUnsavedChanges(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const geocodeAddress = async (address) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=YOUR_GOOGLE_MAPS_API_KEY`
      );
      const data = await response.json();
  
      if (data.status === "OK") {
        const location = data.results[0].geometry.location;
        return {
          coordinates: [location.lng, location.lat],
          address: data.results[0].formatted_address
        };
      }
      throw new Error("Geocoding failed");
    } catch (error) {
      console.error("Geocoding error:", error);
      return null;
    }
  };
  const searchLocation = async (query) => {
    if (!query || query.length < 3) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/serp/maps?q=${encodeURIComponent(query)}`
      );
      if (!response.ok) throw new Error('Failed to fetch locations');
      const data = await response.json();
      setLocationResults(data.local_results || []);
    } catch (error) {
      console.error('Location search error:', error);
    }
  };
  const updateAchievement = (index, updates) => {
    const newAchievements = [...(profile.academicInfo.achievements || [])];
    
    // Combine updates into a single string representation
    const updatedAchievement = JSON.stringify({
      ...(JSON.parse(newAchievements[index] || '{}') || {}),
      ...updates
    });

    newAchievements[index] = updatedAchievement;
    
    handleInputChange("academicInfo", {
      ...profile.academicInfo,
      achievements: newAchievements,
    });
  };

  // Parse achievement string to object
  const parseAchievement = (achievementString) => {
    try {
      return JSON.parse(achievementString) || {};
    } catch {
      return { title: achievementString };
    }
  };

  // Add new achievement
  const addNewAchievement = () => {
    const newAchievement = JSON.stringify({
      title: '',
      issueDate: '',
      description: '',
      certificateImage: ''
    });

    handleInputChange("academicInfo", {
      ...profile.academicInfo,
      achievements: [
        ...(profile.academicInfo.achievements || []),
        newAchievement
      ],
    });
  };

  // Remove achievement
  const removeAchievement = (index) => {
    const newAchievements = profile.academicInfo.achievements.filter((_, i) => i !== index);
    handleInputChange("academicInfo", {
      ...profile.academicInfo,
      achievements: newAchievements,
    });
  };
  // Update debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (profile.personalInfo?.currentLocation?.address) {
        searchLocation(profile.personalInfo.currentLocation.address);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [profile.personalInfo?.currentLocation?.address]);
  const handleInputChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  const handleLocationChange = async (e) => {
    const address = e.target.value;
    handleInputChange("personalInfo", {
      ...profile.personalInfo,
      currentLocation: {
        ...profile.personalInfo.currentLocation,
        address: address
      }
    });
  
    // Debounce geocoding to avoid too many API calls
    if (address.length > 3) {
      const location = await geocodeAddress(address);
      if (location) {
        handleInputChange("personalInfo", {
          ...profile.personalInfo,
          currentLocation: {
            type: "Point",
            coordinates: location.coordinates,
            address: location.address
          }
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Edit Profile
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Update your information to stay connected with the alumni
                  network
                </p>
              </div>
              <button
                onClick={() => navigate("/profile")}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                View Profile
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {(successMessage || error) && (
          <div className="mb-6">
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 text-green-800 p-4 rounded-lg flex items-center"
              >
                <CheckCircle2 className="h-5 w-5 mr-3" />
                {successMessage}
              </motion.div>
            )}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 text-red-800 p-4 rounded-lg flex items-center"
              >
                <AlertCircle className="h-5 w-5 mr-3" />
                {error}
              </motion.div>
            )}
          </div>
        )}

        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar Navigation */}
          <div className="col-span-3">
            <div className="space-y-1">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  {...tab}
                  active={activeTab === tab.id}
                  onClick={setActiveTab}
                />
              ))}
            </div>

            {/* Profile Completion */}
            <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                Profile Completion
              </h3>
              <div className="relative">
                <div className="h-2 bg-gray-100 rounded-full">
                  <div
                    className="h-2 bg-blue-500 rounded-full"
                    style={{ width: "75%" }} // Calculate based on filled fields
                  />
                </div>
                <span className="absolute right-0 top-4 text-sm text-gray-500">
                  75%
                </span>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                Complete your profile to increase visibility in the alumni
                network
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="col-span-9">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {tabs.find((t) => t.id === activeTab).label}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {tabs.find((t) => t.id === activeTab).description}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                {activeTab === "personal" && (
                  <>
                    <FormSection title="Profile Photo">
                      <div className="flex items-start space-x-6">
                      <ImageUpload
  existingImage={profile.personalInfo?.avatar}
  onImageUpload={(url) => handleInputChange('personalInfo', {
    ...profile.personalInfo,
    avatar: url
  })}
  className="w-32 h-32"
  shape="circle"
/>
                        <div className="flex-1">
                          <p className="text-sm text-gray-500">
                            Upload a professional photo for your profile. This
                            will help other alumni recognize you.
                          </p>
                          <ul className="mt-4 text-xs text-gray-500 space-y-1">
                            <li>• PNG, JPG or GIF format</li>
                            <li>• Maximum size of 5MB</li>
                            <li>• Square ratio recommended</li>
                          </ul>
                        </div>
                      </div>
                    </FormSection>

                    <FormSection title="Basic Information">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <InputField
                          label="First Name"
                          value={profile.firstName}
                          onChange={(e) =>
                            handleInputChange("firstName", e.target.value)
                          }
                          icon={User}
                        />
                        <InputField
                          label="Last Name"
                          value={profile.lastName}
                          onChange={(e) =>
                            handleInputChange("lastName", e.target.value)
                          }
                          icon={User}
                        />
                        <InputField
                          label="Contact Number"
                          value={profile.personalInfo?.contactNumber || ""}
                          onChange={(e) =>
                            handleInputChange("personalInfo", {
                              ...profile.personalInfo,
                              contactNumber: e.target.value,
                            })
                          }
                          icon={Phone}
                          type="tel"
                        />
                      <div className="col-span-2">
        <LocationInput
          value={profile.personalInfo?.currentLocation?.address || ""}
          onChange={(value) => {
            handleInputChange("personalInfo", {
              ...profile.personalInfo,
              currentLocation: {
                ...profile.personalInfo?.currentLocation,
                address: value
              }
            });
          }}
          onSelectLocation={(location) => {
            handleInputChange("personalInfo", {
              ...profile.personalInfo,
              currentLocation: location
            });
          }}
        />
        {profile.personalInfo?.currentLocation?.coordinates && (
          <p className="mt-1 text-sm text-gray-500">
            Selected coordinates: {profile.personalInfo.currentLocation.coordinates[1].toFixed(6)}, 
            {profile.personalInfo.currentLocation.coordinates[0].toFixed(6)}
          </p>
        )}
      </div>
                        <InputField
                          label="Alternate Email"
                          value={profile.personalInfo?.alternateEmail || ""}
                          onChange={(e) =>
                            handleInputChange("personalInfo", {
                              ...profile.personalInfo,
                              alternateEmail: e.target.value,
                            })
                          }
                          icon={Mail}
                          type="email"
                        />
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Gender
                          </label>
                          <div className="flex space-x-4">
                            {["male", "female", "other"].map((gender) => (
                              <label
                                key={gender}
                                className="flex items-center space-x-2 cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  name="gender"
                                  value={gender}
                                  checked={
                                    profile.personalInfo?.gender === gender
                                  }
                                  onChange={(e) =>
                                    handleInputChange("personalInfo", {
                                      ...profile.personalInfo,
                                      gender: e.target.value,
                                    })
                                  }
                                  className="form-radio text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 capitalize">
                                  {gender}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </FormSection>
                  </>
                )}

                {/* Academic Information */}
                {activeTab === "academic" && (
                  <>
<FormSection
  title="Academic Details"
  description="Your educational background and timeline"
>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* Degree Selection */}
    <div className="col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        Degree
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
          <GraduationCap className="h-5 w-5 text-gray-400" />
        </div>
        <select
          value={profile.academicInfo?.degree || ""}
          onChange={(e) => {
            handleInputChange("academicInfo", {
              ...profile.academicInfo,
              degree: e.target.value,
              department: "" // Reset department when degree changes
            });
          }}
          className="pl-10 w-full rounded-lg border border-gray-300 bg-white py-2 text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        >
          <option value="">Select Degree</option>
          {profile.college?.degrees?.map((degree) => (
            <option key={degree._id} value={degree.name}>
              {degree.name}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Department Selection */}
    <div className="col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        Department
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
          <Building className="h-5 w-5 text-gray-400" />
        </div>
        <select
          value={profile.academicInfo?.department || ""}
          onChange={(e) => {
            handleInputChange("academicInfo", {
              ...profile.academicInfo,
              department: e.target.value
            });
          }}
          disabled={!profile.academicInfo?.degree}
          className="pl-10 w-full rounded-lg border border-gray-300 bg-white py-2 text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">Select Department</option>
          {profile.college?.degrees
            ?.find(d => d.name === profile.academicInfo?.degree)
            ?.departments.map((dept) => (
              <option key={dept._id} value={dept.name}>
                {dept.name}
              </option>
            ))}
        </select>
      </div>
    </div>

    {/* Rest of your academic fields */}


  <InputField
    label="Enrollment Number"
    value={profile.enrollmentNumber}
    onChange={(e) => handleInputChange("enrollmentNumber", e.target.value)}
    icon={Hash}
    placeholder="e.g., 21BECE30351"
  />
  
  <InputField
    label="Admission Year"
    type="number"
    value={profile.admissionYear || ""}
    onChange={(e) =>
      handleInputChange("admissionYear", e.target.value)
    }
    icon={Calendar}
    placeholder="e.g., 2021"
    helper="Year you were admitted to the college"
  />
  
  <InputField
    label="Start Year"
    type="number"
    value={profile.academicInfo?.batch?.startYear || ""}
    onChange={(e) =>
      handleInputChange("academicInfo", {
        ...profile.academicInfo,
        batch: { startYear: e.target.value },
      })
    }
    icon={Calendar}
    placeholder="e.g., 2021"
  />
  
  <InputField
    label="Graduation Year"
    type="number"
    value={profile.graduationYear || ""}
    onChange={(e) =>
      handleInputChange("graduationYear", e.target.value)
    }
    icon={GraduationCap}
    placeholder="e.g., 2025"
  />
    
  
  </div>
</FormSection>

                    <FormSection
      title="Achievements"
      description="Add your academic achievements, awards, and certifications"
    >
      <div className="space-y-4">
        {profile.academicInfo?.achievements?.map((achievementString, index) => {
          const achievement = parseAchievement(achievementString);
          
          return (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 border p-4 rounded-lg relative"
            >
              {/* Remove Achievement Button */}
              <button
                type="button"
                onClick={() => removeAchievement(index)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Achievement Title */}
              <InputField
                label="Achievement Title"
                value={achievement.title || ''}
                onChange={(e) => updateAchievement(index, { title: e.target.value })}
                icon={Trophy}
                placeholder="e.g., NPTEL Certificate"
              />

              {/* Issue Date */}
              <InputField
                label="Issue Date"
                type="date"
                value={achievement.issueDate || ''}
                onChange={(e) => updateAchievement(index, { issueDate: e.target.value })}
                icon={Calendar}
              />

              {/* Description - Full Width */}
              <div className="col-span-2">
                <InputField
                  label="Description"
                  value={achievement.description || ''}
                  onChange={(e) => updateAchievement(index, { description: e.target.value })}
                  icon={Book}
                  placeholder="Provide more details about your achievement"
                />
              </div>

              {/* Certificate Upload */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certificate Image
                </label>
                <ImageUpload
                  onImageUpload={(url) => updateAchievement(index, { certificateImage: url })}
                  existingImage={achievement.certificateImage}
                  className="h-40 w-full"
                />
              </div>
            </div>
          );
        })}

       
      </div>
    </FormSection>
                  </>
                )}

                {/* Professional Information */}
                {activeTab === "professional" && (
                  <>
                   <FormSection
      title="Employment Status"
      description="Update your current employment status"
    >
      <div className="space-y-4">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={profile.professionalInfo?.currentlyEmployed || false}
            onChange={(e) => {
              handleInputChange("professionalInfo", {
                ...profile.professionalInfo,
                currentlyEmployed: e.target.checked
              });
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
          />
          <span className="text-sm text-gray-700">I am currently employed</span>
        </label>
      </div>
    </FormSection>

    <FormSection
      title="Work Experience"
      description="Add your professional experience and work history"
    >
                      <div className="space-y-6">
                        {profile.professionalInfo?.experiences?.map(
                          (exp, index) => (
                            <div
                              key={index}
                              className="relative bg-gray-50 rounded-lg p-6 border border-gray-200"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  const newExperiences =
                                    profile.professionalInfo.experiences.filter(
                                      (_, i) => i !== index
                                    );
                                  handleInputChange("professionalInfo", {
                                    ...profile.professionalInfo,
                                    experiences: newExperiences,
                                  });
                                }}
                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
                              >
                                <X className="h-5 w-5" />
                              </button>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <InputField
                                  label="Company Name"
                                  value={exp.company || ""}
                                  onChange={(e) => {
                                    const newExperiences = [
                                      ...profile.professionalInfo.experiences,
                                    ];
                                    newExperiences[index] = {
                                      ...exp,
                                      company: e.target.value,
                                    };
                                    handleInputChange("professionalInfo", {
                                      ...profile.professionalInfo,
                                      experiences: newExperiences,
                                    });
                                  }}
                                  icon={Building}
                                  placeholder="e.g., Google"
                                />
                                <InputField
                                  label="Job Title"
                                  value={exp.title || ""}
                                  onChange={(e) => {
                                    const newExperiences = [
                                      ...profile.professionalInfo.experiences,
                                    ];
                                    newExperiences[index] = {
                                      ...exp,
                                      title: e.target.value,
                                    };
                                    handleInputChange("professionalInfo", {
                                      ...profile.professionalInfo,
                                      experiences: newExperiences,
                                    });
                                  }}
                                  icon={Briefcase}
                                  placeholder="e.g., Software Engineer"
                                />
                                <InputField
                                  label="Location"
                                  value={exp.location || ""}
                                  onChange={(e) => {
                                    const newExperiences = [
                                      ...profile.professionalInfo.experiences,
                                    ];
                                    newExperiences[index] = {
                                      ...exp,
                                      location: e.target.value,
                                    };
                                    handleInputChange("professionalInfo", {
                                      ...profile.professionalInfo,
                                      experiences: newExperiences,
                                    });
                                  }}
                                  icon={MapPin}
                                  placeholder="e.g., San Francisco, CA"
                                />
                                <div className="flex space-x-4">
                                  <div className="flex-1">
                                    <InputField
                                      label="Start Date"
                                      type="date"
                                      value={
                                        exp.startDate
                                          ? new Date(exp.startDate)
                                              .toISOString()
                                              .split("T")[0]
                                          : ""
                                      }
                                      onChange={(e) => {
                                        const newExperiences = [
                                          ...profile.professionalInfo
                                            .experiences,
                                        ];
                                        newExperiences[index] = {
                                          ...exp,
                                          startDate: e.target.value,
                                        };
                                        handleInputChange("professionalInfo", {
                                          ...profile.professionalInfo,
                                          experiences: newExperiences,
                                        });
                                      }}
                                      icon={Calendar}
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <InputField
                                      label="End Date"
                                      type="date"
                                      value={
                                        exp.endDate
                                          ? new Date(exp.endDate)
                                              .toISOString()
                                              .split("T")[0]
                                          : ""
                                      }
                                      onChange={(e) => {
                                        const newExperiences = [
                                          ...profile.professionalInfo
                                            .experiences,
                                        ];
                                        newExperiences[index] = {
                                          ...exp,
                                          endDate: e.target.value,
                                        };
                                        handleInputChange("professionalInfo", {
                                          ...profile.professionalInfo,
                                          experiences: newExperiences,
                                        });
                                      }}
                                      disabled={exp.isCurrent}
                                      icon={Calendar}
                                    />
                                  </div>
                                </div>
                                <div className="col-span-2">
                                  <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={exp.isCurrent || false}
                                      onChange={(e) => {
                                        const newExperiences = [
                                          ...profile.professionalInfo
                                            .experiences,
                                        ];
                                        newExperiences[index] = {
                                          ...exp,
                                          isCurrent: e.target.checked,
                                        };
                                        handleInputChange("professionalInfo", {
                                          ...profile.professionalInfo,
                                          experiences: newExperiences,
                                        });
                                      }}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">
                                      I currently work here
                                    </span>
                                  </label>
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                  </label>
                                  <textarea
                                    value={exp.description || ""}
                                    onChange={(e) => {
                                      const newExperiences = [
                                        ...profile.professionalInfo.experiences,
                                      ];
                                      newExperiences[index] = {
                                        ...exp,
                                        description: e.target.value,
                                      };
                                      handleInputChange("professionalInfo", {
                                        ...profile.professionalInfo,
                                        experiences: newExperiences,
                                      });
                                    }}
                                    rows={4}
                                    className="w-full rounded-lg border-gray-300 focus:ring-blue-500"
                                    placeholder="Describe your responsibilities and achievements..."
                                  />
                                </div>
                              </div>
                            </div>
                          )
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            const newExperience = {
                              company: "",
                              title: "",
                              location: "",
                              startDate: "",
                              endDate: "",
                              isCurrent: false,
                              description: "",
                            };
                            handleInputChange("professionalInfo", {
                              ...profile.professionalInfo,
                              experiences: [
                                ...(profile.professionalInfo.experiences || []),
                                newExperience,
                              ],
                            });
                          }}
                          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 flex items-center justify-center"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Add Work Experience
                        </button>
                      </div>
                    </FormSection>

                    <FormSection
                      title="Skills"
                      description="Add your technical and professional skills"
                    >
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {profile.professionalInfo?.skills?.map(
                            (skill, index) => (
                              <div
                                key={index}
                                className="inline-flex items-center bg-blue-50 text-blue-700 rounded-full px-3 py-1"
                              >
                                <span className="text-sm">{skill}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newSkills =
                                      profile.professionalInfo.skills.filter(
                                        (_, i) => i !== index
                                      );
                                    handleInputChange("professionalInfo", {
                                      ...profile.professionalInfo,
                                      skills: newSkills,
                                    });
                                  }}
                                  className="ml-2 text-blue-500 hover:text-blue-700"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <InputField
                            placeholder="Add a skill (e.g., React.js, Project Management)"
                            value={newSkill}
                            onChange={(e) => setNewSkill(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter" && newSkill.trim()) {
                                handleInputChange("professionalInfo", {
                                  ...profile.professionalInfo,
                                  skills: [
                                    ...(profile.professionalInfo.skills || []),
                                    newSkill.trim(),
                                  ],
                                });
                                setNewSkill("");
                                e.preventDefault();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newSkill.trim()) {
                                handleInputChange("professionalInfo", {
                                  ...profile.professionalInfo,
                                  skills: [
                                    ...(profile.professionalInfo.skills || []),
                                    newSkill.trim(),
                                  ],
                                });
                                setNewSkill("");
                              }
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </FormSection>
                  </>
                )}

                {/* Social Links */}
                {activeTab === "social" && (
                  <FormSection
                    title="Social Profiles"
                    description="Connect your social media profiles and professional networks"
                  >
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-6">
                        {[
                          {
                            key: "linkedin",
                            icon: Linkedin,
                            label: "LinkedIn Profile",
                            placeholder: "https://linkedin.com/in/username",
                            color: "blue",
                          },
                          {
                            key: "github",
                            icon: Github,
                            label: "GitHub Profile",
                            placeholder: "https://github.com/username",
                            color: "gray",
                          },
                          {
                            key: "twitter",
                            icon: Twitter,
                            label: "Twitter Profile",
                            placeholder: "https://twitter.com/username",
                            color: "sky",
                          },
                          {
                            key: "portfolio",
                            icon: Globe,
                            label: "Portfolio Website",
                            placeholder: "https://yourwebsite.com",
                            color: "purple",
                          },
                        ].map(
                          ({ key, icon: Icon, label, placeholder, color }) => (
                            <div key={key}>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {label}
                              </label>
                              <div className="mt-1 flex rounded-lg shadow-sm">
                                <span
                                  className={`inline-flex items-center px-4 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-${color}-500`}
                                >
                                  <Icon className="h-5 w-5" />
                                </span>
                                <input
                                  type="url"
                                  value={profile.socialLinks?.[key] || ""}
                                  onChange={(e) =>
                                    handleInputChange("socialLinks", {
                                      ...profile.socialLinks,
                                      [key]: e.target.value,
                                    })
                                  }
                                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder={placeholder}
                                />
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </FormSection>
                )}
                {activeTab === "security" && (
  <FormSection
    title="Change Password"
    description="Update your password to keep your account secure"
  >
    <PasswordChangeSection />
  </FormSection>
)}

                {/* Save Button - Fixed at bottom */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-4 px-8">
                  <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      {hasUnsavedChanges && (
                        <span className="flex items-center text-yellow-600">
                          <AlertCircle className="h-4 w-4 mr-1.5" />
                          You have unsaved changes
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => navigate("/profile")}
                        className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={saving || !hasUnsavedChanges}
                        className={`
                          inline-flex items-center px-4 py-2 border border-transparent 
                          rounded-lg shadow-sm text-sm font-medium text-white
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                          ${
                            saving || !hasUnsavedChanges
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700"
                          }
                        `}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                            Saving Changes...
                          </>
                        ) : (
                          <>
                            <Save className="-ml-1 mr-2 h-4 w-4" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;